import { Users, Clock, CalendarPlus, CalendarClock } from 'lucide-react'
import { format, min, max } from 'date-fns'
import { useMemo } from 'react'
import type { InterviewNotesItem } from '@bandos/shared'
import { MetaRow, MetaBadge } from '../../survey/pages/SurveyInsightsPageStyles'

export interface InterviewInsightsMetaProps {
  createdAt: string
  notes: InterviewNotesItem[]
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

export function InterviewInsightsMeta({ createdAt, notes }: InterviewInsightsMetaProps) {
  const count = notes.length

  const createdLabel = useMemo(() => format(new Date(createdAt), 'MMM d, yyyy'), [createdAt])

  const notesMeta = useMemo(() => {
    if (count === 0) return null
    const dates = notes.map((r) => new Date(r.savedAt))
    const earliest = format(min(dates), 'MMM d, yyyy')
    const latest = format(max(dates), 'MMM d, yyyy')

    const validDurations = notes
      .map((r) => r.durationSeconds)
      .filter((d): d is number => typeof d === 'number' && d > 0)

    const avgDuration =
      validDurations.length > 0
        ? Math.round(validDurations.reduce((s, d) => s + d, 0) / validDurations.length)
        : null

    return { earliest, latest, avgDuration }
  }, [count, notes])

  return (
    <MetaRow>
      <MetaBadge title="When this interview was created">
        <CalendarPlus size={13} />
        Created {createdLabel}
      </MetaBadge>
      <MetaBadge>
        <Users size={13} />
        {count} interview{count !== 1 ? 's' : ''}
      </MetaBadge>
      {notesMeta && (
        <MetaBadge title="When interviews were recorded">
          <CalendarClock size={13} />
          Recorded{' '}
          {notesMeta.earliest === notesMeta.latest
            ? notesMeta.earliest
            : `${notesMeta.earliest} – ${notesMeta.latest}`}
        </MetaBadge>
      )}
      {notesMeta?.avgDuration != null && (
        <MetaBadge>
          <Clock size={13} />
          {formatDuration(notesMeta.avgDuration)} avg duration
        </MetaBadge>
      )}
    </MetaRow>
  )
}
